'use client'
import { CMSLink } from '@/components/Link'
import { Cart } from '@/components/Cart'
import { OpenCartButton } from '@/components/Cart/OpenCart'
import Link from 'next/link'
import React, { Suspense } from 'react'
import { ArrowRightIcon, ArrowLeftIcon } from 'lucide-react'

import { MobileMenu } from './MobileMenu'
import type { Header } from 'src/payload-types'

import { Logo } from '@/components/brand/Logo'
import { usePathname } from 'next/navigation'
import { cn } from '@/utilities/cn'
import { zoneOf } from '@/utilities/zone'

type Props = {
  header: Header
}

export function HeaderClient({ header }: Props) {
  const pathname = usePathname()
  const zone = zoneOf(pathname)
  const menu = (zone === 'shop' ? header.shopNav : header.clubNav) || []

  return (
    <div className="sticky top-0 z-20 bg-sand/90 backdrop-blur border-b border-ink/10">
      <nav className="container flex items-center justify-between gap-4 h-16 md:h-20">
        <div className="flex-none md:hidden">
          <Suspense fallback={null}>
            <MobileMenu menu={menu} zone={zone} />
          </Suspense>
        </div>

        <Link className="flex items-center gap-3 text-ink" href={zone === 'shop' ? '/shop' : '/'} aria-label="UtroClub">
          <Logo variant="sun" className="h-9 w-auto md:h-11" />
          <span className="flex items-baseline gap-2">
            <Logo variant="wordmark" className="h-5 w-auto md:h-6" />
            {zone === 'shop' && <span className="hidden font-display text-xs uppercase tracking-wide text-punch sm:inline">Мерч</span>}
          </span>
        </Link>

        {menu.length ? (
          <ul className="hidden md:flex items-center gap-8 font-display text-sm uppercase tracking-wide">
            {menu.map((item) => {
              const active = item.link.url && item.link.url !== '/' ? pathname.startsWith(item.link.url) : pathname === '/'
              return (
                <li key={item.id}>
                  <CMSLink
                    {...item.link}
                    size={'clear'}
                    appearance="nav"
                    className={cn(
                      'relative pb-1 transition-colors hover:text-punch',
                      'after:absolute after:left-0 after:-bottom-0.5 after:h-[3px] after:w-full after:bg-sun after:origin-left after:scale-x-0 after:transition-transform',
                      { 'after:scale-x-100': active },
                    )}
                  />
                </li>
              )
            })}
          </ul>
        ) : null}

        <div className="flex items-center gap-3">
          {/* кросс-ссылка между зонами — всегда на виду */}
          {zone === 'club' ? (
            <Link
              href="/shop"
              className="hidden items-center gap-1.5 rounded-full border-2 border-ink px-4 h-10 font-display text-xs uppercase tracking-wide text-ink transition-colors hover:bg-ink hover:text-sun sm:inline-flex"
            >
              Мерч
              <ArrowRightIcon className="h-3.5 w-3.5" />
            </Link>
          ) : (
            <Link
              href="/"
              className="hidden items-center gap-1.5 font-display text-xs uppercase tracking-wide text-ink/70 transition-colors hover:text-punch sm:inline-flex"
            >
              <ArrowLeftIcon className="h-3.5 w-3.5" />
              Клуб
            </Link>
          )}

          <Suspense fallback={<OpenCartButton quiet={zone === 'club'} />}>
            <Cart quiet={zone === 'club'} />
          </Suspense>
        </div>
      </nav>
    </div>
  )
}
